/**
 * Circular dependency prevention:
 * - useBasket
 * - usePerson
 */
import { computed, reactive, ref } from 'vue'
import { cloneDeep, get, isEmpty } from 'lodash'

import { CATEGORY_TYPE, CHANNEL, SALES_PARTNER_TYPE } from '@/config/constants'
import { productAVMtypes } from '@/components/MedicalSearch/config/constants'
import { EVENT_BASKET, EVENT_PRODUCT } from '@/config/events'

import { isRequired, MissingParameterError, NotFoundError } from '@/utils/CustomErrors'
import { SortUtil } from '@/utils/Sorting'
import { DateTimeUtil } from '@/utils/DateTime'
import events$ from '@/services/Events'
import config from '@/config'

import useBackend from '@/hooks/useBackend'
import useBasket from '@/hooks/useBasket'
import useCategories from '@/hooks/useCategories'
import useFranchise from '@/hooks/useFranchise'
import useLogger from '@/hooks/useLogger'
import usePartner from '@/hooks/usePartner'
import usePerson from '@/hooks/usePerson'
import useI18n from '@/hooks/useI18n'

// Hooks
const { setup, doGet, doPost } = useBackend()
const { getCategory, getGroup } = useCategories()
const { formatCurrency } = useFranchise()
const { logInfo } = useLogger()
const { partner } = usePartner()

setup('/ovpv3/public', config.microserviceBaseUrl, {
  responseSuccess: response => {
    return response.data
  },
  responseError: error => {
    return Promise.reject(error)
  },
})

// Data
const loaded = ref(false)
const productData = reactive({
  categories: {},
  groups: {},
  products: {},
})

// Events
events$.on(EVENT_BASKET.UPDATED, ({ basket: __basket, originalBasket }) => {
  initProducts(__basket, originalBasket)
})

// Computed
const categories = computed(() => {
  return productData.categories
})

const groups = computed(() => {
  return productData.groups
})

const products = computed(() => {
  const data = cloneDeep(productData.products)

  Object.keys(productData.products).forEach(__productId => {
    mapIdToProductId(data, __productId)
  })

  return data
})

const selectedCategories = computed(() => {
  const { persons } = usePerson()
  // eslint-disable-next-line no-shadow
  const { hasPersonOnlyKVG, hasPersonOnlyVVG } = useProduct()

  if (persons.value.length > 0) {
    return persons.value.reduce((result, p) => {
      result[p.personId] = {
        isKVGOnly: hasPersonOnlyKVG(p.personId),
        isVVGOnly: hasPersonOnlyVVG(p.personId),
      }
      return result
    }, {})
  }
  return []
})

const hasSelectedProducts = computed(() => {
  const { persons } = usePerson()
  if (persons.value.length === 0) return false

  return (
    persons.value.filter(person => {
      return (
        Object.keys(person.products.products).filter(productId => person.products.products[productId].selected).length >
        0
      )
    }).length > 0
  )
})

const selectedProducts = computed(() => {
  const { persons } = usePerson()

  if (persons.value.length === 0) return {}

  const people = {}
  persons.value.forEach(person => {
    const _selectedProducts = Object.keys(person.products.products)
      .filter(productId => person.products.products[productId].selected)
      .reduce((__products, productId) => {
        const selectedProduct = person.products.products[productId]

        __products.push(
          Object.assign({}, selectedProduct, {
            productId,
            price: selectedProduct.prices.find(p => p.selected),
            order: products.value[productId]?.order,
          })
        )

        return __products
      }, [])

    people[person.personId] = _selectedProducts || []
  })

  return people
})

// Methods
async function setPriceCalculationsForProduct(productId, person) {
  const { basket } = useBasket()
  const selectedProduct = getProduct(person.personId, productId)
  const selectedPrice = selectedProduct.prices.find(p => p.selected)
  const payload = {
    address: basket.address,
    collective: basket.collective,
    collectivePersonGroup: person.collectivePersonGroup,
    contractNumber: basket.contractNumber,
    contractStartDate: basket.contractStartDate,
    dateOfBirth: person.personData.dateOfBirth,
    gender: person.personData.gender,
    masterTreaty: basket.masterTreaty,
    selectedPriceCombination: selectedPrice,
    zsrNumber: selectedProduct.doctor?.zsrNumber,
  }
  const priceCalculations = await doPost(`products/${productId}/calculations`, payload)
  return priceCalculations.data
}

function selectProductOptionForCategory(personId, categoryId, option) {
  const data = {
    products: [],
    mutations: [],
  }

  getGroupsFromCategory(personId, categoryId)
    .reduce((acc, val) => acc.concat(val.products), [])
    .forEach(productId => {
      data.products.push(productId)
      data.mutations.push(selectProductOption(personId, productId, option))
    })

  return Promise.all(data.mutations).then(fragments => {
    const __products = fragments.reduce((acc, value, index) => {
      const productId = data.products[index]
      acc[productId] = value.persons.find(p => p.personId === personId).products.products[productId]
      return acc
    }, {})

    const __basket = fragments[0]
    const personIndex = __basket.persons.findIndex(p => p.personId === personId)

    __basket.persons[personIndex].products.products = Object.assign(
      {},
      __basket.persons[personIndex].products.products,
      __products
    )

    return Promise.resolve(__basket)
  })
}

async function selectProductOption(personId, productId, option, __basket = null) {
  const { updateBasketForPerson } = useBasket()
  const { getPerson } = usePerson()

  const pBasket = __basket ? __basket.persons.find(p => p.personId === personId) : getPerson(personId)
  const product = pBasket.products.products[productId]

  product.prices = product.prices.map(item => {
    if (
      item.amValue &&
      item.gfValue &&
      item.amValue === option.amValue &&
      item.gfValue === option.gfValue &&
      item.duration === option.duration
    ) {
      item.selected = option.selected
    } else if (item.value !== undefined && item.value === option.value && item.duration === option.duration) {
      item.selected = option.selected
    } else if (item.value === undefined && item.duration === option.duration) {
      item.selected = option.selected
    } else {
      item.selected = false
    }

    if (option.toggle !== undefined) {
      item.toggle = option.toggle
    }
    return item
  })

  return await updateBasketForPerson(personId, pBasket, { basket: __basket })
}

function sortProducts(value, __products) {
  return value
    .filter(product => !!__products[product.productId])
    .map(product => Object.assign(product, __products[product.productId]))
    .sort(SortUtil.sortByOrder)
}

async function setProducts(payload) {
  Object.keys(payload).forEach(key => {
    productData[key] = payload[key]
  })

  events$.emit(EVENT_PRODUCT.STRUCTURE_SET, payload)
  logInfo(['%cPRODUCTS SET', 'color: green', payload])

  return await Promise.resolve(payload)
}

async function transferProductToPerson(sourceId, targetId, productId, options = {}) {
  const { addProductToOvpBasket } = useBasket()
  const { getPerson, persons } = usePerson()
  const __basket = options.basket ?? null
  const mode = options.mode ?? 'REPLACE'
  const doctor = options.doctor ?? null
  const __person = __basket ? persons.value.find(p => p.personId === sourceId) : getPerson(sourceId)
  const product = __person.products.products[productId]
  const option = product.prices.find(p => p.selected)

  const updatedBasket = await addProductToOvpBasket(targetId, productId, { basket: __basket, doctor, mode })
  return await selectProductOption(targetId, productId, option, updatedBasket)
}

function getSelectedProductsOfPerson(personProducts = isRequired('selected products')) {
  const mergeProduct = productId => Object.assign({ productId }, products.value[productId], personProducts[productId])
  const replacePrice = product => {
    product.price = product.prices.find(p => !!p.selected)
    return product
  }

  return Object.keys(personProducts)
    .map(productId => mergeProduct(productId))
    .filter(product => product.selected)
    .map(product => replacePrice(product))
}

function isProductInBasket(personId = isRequired('personId'), productId = isRequired('productId')) {
  const { getPerson } = usePerson()
  const person = getPerson(personId)
  return !!person.products.products[productId].selected
}

function getAllContractStartDates(__basket) {
  const contractStartDates = new Set()
  if (__basket.persons) {
    __basket.persons.forEach(p => {
      contractStartDates.add(p.kvgContractStartDate)
      contractStartDates.add(p.vvgContractStartDate)
    })
  }
  return contractStartDates
}

function hasContractStartDateChanged(newBasket, oldBasket) {
  const newContractStartDates = getAllContractStartDates(newBasket)
  const oldContractStartDates = getAllContractStartDates(oldBasket)

  if (newContractStartDates.size !== oldContractStartDates.size) {
    return true
  }

  for (const startDate of newContractStartDates) {
    if (!oldContractStartDates.has(startDate)) {
      return true
    }
  }

  return false
}

function isProductReloadNecessary(newBasket, oldBasket) {
  return (
    newBasket?.collective?.contractNumber !== oldBasket.collective?.contractNumber ||
    hasContractStartDateChanged(newBasket, oldBasket) ||
    newBasket?.language !== oldBasket.language
  )
}

async function fetchProducts(__basket) {
  if (!__basket?.masterTreaty) throw new MissingParameterError('masterTreaty')

  const startDates = getAllContractStartDates(__basket)
  const payload = {
    collectiveChannel: __basket.collective?.channel,
    collectiveContractNumber: __basket.collective?.contractNumber,
    contractStartDates: Array.from(startDates),
    masterTreaty: __basket.masterTreaty,
  }

  const response = await doGet('products', payload)
  return response.data
}

function composeOptions(product, groupId, categoryId) {
  const { t } = useI18n()

  const optionTitles = getOptionTitles(product.productId, groupId, categoryId)
  const price = product.prices.find(p => p.selected)

  const options = []
  if (hasAccident(product)) {
    options.push(t(`product.accident.${price.toggle ? 'on' : 'off'}`))
  }

  // @note for now, only for the vital-product (m)
  if (hasVitalFranchise(product)) {
    options.push(
      `${optionTitles.amValue.title} ${formatCurrency(price.amValue, null, 0)}`,
      `${optionTitles.gfValue.title} ${formatCurrency(price.gfValue, null, 0)}`
    )
  }

  // @note do not use `hasFranchise` here, as that is used to check, if franchise-selection needs to be display
  if (price.value !== undefined) {
    const franchise = `${optionTitles.franchise.title} ${formatCurrency(price.value, null, 0)} `
    options.push(franchise)
  }

  if (hasDuration(product) && optionTitles.duration) {
    switch (price.duration.toString()) {
      case '1':
        options.push(t('product.contractDuration.single', { year: price.duration }))
        break

      case '3':
      case '5':
        options.push(t('product.contractDuration.multiple', { years: price.duration }))
        break

      case 'F':
        options.push(t('product.contractDuration.extended'))
        break
    }
  }

  return options.join(', ')
}

async function createDefaultOffer() {
  const { basket, updateOvpBasket } = useBasket()
  const __basket = cloneDeep(basket)
  const productIds = await doPost('products/defaultOfferProductIds', __basket)

  __basket.persons.forEach(person => {
    Object.keys(person.products.products).forEach(productId => {
      person.products.products[productId].selected = productIds.data[person.personId].includes(productId)
    })
  })
  __basket.channel = CHANNEL.ALVA
  return await updateOvpBasket(__basket)
}

// @TODO try to pass selectedProducts directly
function hasPersonOnlyKVG(personId) {
  const { getPersonWithDetails, getSelectedProductsByCategory } = usePerson()
  const __selectedProducts = getPersonWithDetails(personId).selectedProducts
  const productCategories = getSelectedProductsByCategory(__selectedProducts)
  return productCategories.length === 1 && productCategories[0].id === CATEGORY_TYPE.KVG
}

// @TODO try to pass selectedProducts directly
function hasPersonOnlyVVG(personId) {
  const { getPersonWithDetails, getSelectedProductsByCategory } = usePerson()
  const __selectedProducts = getPersonWithDetails(personId).selectedProducts
  const productCategories = getSelectedProductsByCategory(__selectedProducts)
  return productCategories.length > 0 && !productCategories.find(category => category.id === CATEGORY_TYPE.KVG)
}

function hasPersonKVG(__selectedProducts) {
  const { getSelectedProductsByCategory } = usePerson()
  const productCategories = getSelectedProductsByCategory(__selectedProducts)
  return productCategories.some(category => category.id === CATEGORY_TYPE.KVG)
}

function hasPersonVVG(__selectedProducts) {
  const { getSelectedProductsByCategory } = usePerson()
  const productCategories = getSelectedProductsByCategory(__selectedProducts)
  return productCategories.some(category =>
    [CATEGORY_TYPE.HOSP, CATEGORY_TYPE.ADDITIONAL, CATEGORY_TYPE.FURTHER_ADDITIONAL].includes(category.id)
  )
}

function hasAccident(product) {
  // eslint-disable-next-line no-prototype-builtins
  return product.prices.find(p => p.selected)?.hasOwnProperty('toggle')
}

function hasAccidentToggle({ categoryId, dateOfBirth, kvgContractStartDate, product }) {
  if (product && !hasAccident(product)) return false
  if (categoryId !== CATEGORY_TYPE.KVG) return true

  // FLEX-1303 Age at the end of year of contract start date >= 16 -> accident always true for KVG and toggle hidden
  const ageEndOfContractStartYear = DateTimeUtil.getAgeEndOfContractStartYear(dateOfBirth, kvgContractStartDate)
  return ageEndOfContractStartYear >= 16 && ageEndOfContractStartYear <= 65
}

function hasDuration(product) {
  const isDurationVisibleForPartners = [
    SALES_PARTNER_TYPE.PRIVATE_INSURER,
    SALES_PARTNER_TYPE.BROKER,
    SALES_PARTNER_TYPE.MOBILE,
  ].includes(partner.value.base?.salesPartnerType)

  // eslint-disable-next-line no-prototype-builtins
  const hasProp = product.prices.find(p => p.selected)?.hasOwnProperty('duration')

  return (isDurationVisibleForPartners || product.productId === 'SBVB01Produkt') && hasProp
}

/**
 * @NOTE Is used to know, if the franchise-selection needs to be displayed.
 * If you need to check, if there's a value, check for value on the selected options on a product
 */
function hasFranchise(product) {
  const values = []
  product.prices.forEach(p => {
    values.push(p.value)
  })
  return [...new Set(values)].length > 1
}

function hasVitalFranchise(product) {
  return product.prices.find(p => !!p.amValue && !!p.gfValue)
}

function hasOptions(product) {
  return hasAccident(product) || hasFranchise(product) || hasDuration(product) || hasVitalFranchise(product)
}

function isUpgrade(productId = isRequired('productId')) {
  return Object.keys(products.value).find(_productId => products.value[_productId].upgrades.includes(productId))
}

function getParentProduct(productId = isRequired('productId')) {
  return Object.keys(productData.products).find(__productId => {
    return products.value[__productId].upgrades.find(__upgradeId => __upgradeId === productId)
  })
}

/**
 * check, if products need to be loaded
 * caveat: also get's called on initial local basket-initialization, but cancels, because masterTreaty is missing
 */
async function initProducts(__basket, originalBasket) {
  if (__basket?.persons?.length === 0) return false
  const __reloadNecessary = !!isProductReloadNecessary(__basket, originalBasket)

  if ((__reloadNecessary || loaded.value === false) && __basket.masterTreaty) {
    const structure = await fetchProducts(__basket)
    if (structure) {
      loaded.value = true
      await setProducts(structure)
    }
  }
}

/**
 * The backend used id whereas the frontend works with productId everywhere. This function provides the mapping logic.
 * @param __products Set of products
 * @param __productId Id to identify the product
 */
function mapIdToProductId(__products, __productId) {
  __products[__productId].productId = __productId
  delete __products[__productId].id
}

/**
 * Get the categoryId from a product. If the passed product is an upgrade,
 * the productId if it's parent will be used
 *
 * @param {String} productId
 *
 * @return {String} categoryId
 */
function getCategoryIdFromProduct(productId = isRequired('productId')) {
  if (!productData?.categories) throw new MissingParameterError('categories')
  if (!productData?.groups) throw new MissingParameterError('groups')

  const { categories: rawCategories, groups: rawGroups } = productData

  if (!rawCategories) return undefined

  let __categoryId
  let __productId = productId

  if (isUpgrade(productId)) {
    __productId = getParentProduct(productId)
  }

  Object.keys(rawCategories).find(categoryId => {
    const _match = rawCategories[categoryId].groups.find(groupId => {
      return rawGroups[groupId].products.includes(__productId)
    })

    if (_match) {
      __categoryId = categoryId
    }
  })

  return __categoryId
}

function getProduct(personId, productId) {
  const { getPerson } = usePerson()
  return getPerson(personId)?.products?.products[productId]
}

function getProductPrice(personId, productId) {
  return getProduct(personId, productId).prices.find(p => !!p.selected)
}

function getGroupsFromCategory(personId, categoryId) {
  const { getPerson } = usePerson()
  const __groups = getPerson(personId)?.products?.groups ?? {}

  return getProductGroupsFromCategory(categoryId)
    .filter(group => !!__groups[group.id])
    .map(group => Object.assign(group, __groups[group.id]))
    .sort(SortUtil.sortByOrder)
}

function getGroupFromProduct(productId) {
  return groups.value[getGroupIdFromProduct(productId)]
}

/**
 * Get the groupId from a product. If the passed product is an upgrade,
 * the productId if it's parent will be used
 *
 * @param {String} productId
 *
 * @return {String} groupId
 */
function getGroupIdFromProduct(productId = isRequired('productId')) {
  let __productId = productId

  if (isUpgrade(productId)) {
    __productId = getParentProduct(productId)
  }

  return Object.keys(productData.groups).find(groupId => productData.groups[groupId].products.includes(__productId))
}

function getProductsFromGroup(groupId, personId) {
  const __products = personId ? getPersonProducts(personId) : products.value
  return sortProducts(getSortedProductsFromGroup(groupId), __products)
}

function getProductGroupsFromCategory(categoryId) {
  return getSortedGroupsFromCategory(productData.categories, productData.groups, categoryId)
}

/**
 * Returns all groups of a category sorted by the order field
 * @param __categories
 * @param __groups
 * @param categoryId
 * @return []
 */
function getSortedGroupsFromCategory(
  __categories = isRequired('categories'),
  __groups = isRequired('groups'),
  categoryId = isRequired('categoryId')
) {
  if (!isEmpty(__categories) && !__categories[categoryId])
    throw new NotFoundError('category with categoryId', categoryId)

  if (!__categories[categoryId]?.groups) return []

  const groupsCopy = cloneDeep(__groups)
  return __categories[categoryId].groups.map(groupId => groupsCopy[groupId]).sort(SortUtil.sortByOrder) || []
}

function getProductsFromCategory(categoryId, personId) {
  const __products = personId ? getPersonProducts(personId) : products.value
  return sortProducts(getSortedProductsFromCategory(categoryId), __products)
}

function getPersonProducts(personId) {
  const { getPerson } = usePerson()
  return getPerson(personId)?.products?.products ?? {}
}

/**
 * Returns all existing products of a category sorted by the order field
 * @param categoryId
 * @return []
 */
function getSortedProductsFromCategory(categoryId = isRequired('categoryId')) {
  if (!productData?.categories) throw new MissingParameterError('categories')
  if (!productData?.groups) throw new MissingParameterError('groups')
  if (!productData?.products) throw new MissingParameterError('products')

  const { categories: rawCategories, groups: rawGroups, products: rawProducts } = productData

  if (!rawCategories[categoryId]) throw new NotFoundError('category with categoryId', categoryId)
  return rawCategories[categoryId].groups
    .reduce((acc, groupId) => acc.concat(_getSortedProductsFromGroup(rawGroups, rawProducts, groupId)), [])
    .sort(SortUtil.sortByOrder)
}

function getSortedProductsFromGroup(groupId) {
  return _getSortedProductsFromGroup(productData.groups, products.value, groupId)
}

/**
 * Returns all existing products of a group sorted by the order field
 * @param __groups
 * @param __products
 * @param groupId
 * @return []
 */
function _getSortedProductsFromGroup(
  __groups = isRequired('groups'),
  __products = isRequired('products'),
  groupId = isRequired('groupId')
) {
  let groupsObject = { ...__groups }
  if (!groupsObject[groupId]) throw new NotFoundError('group with groupId', groupId)
  const productsCopy = cloneDeep(__products)
  return groupsObject[groupId].products
    .map(productId => {
      mapIdToProductId(productsCopy, productId)
      return productsCopy[productId]
    })
    .filter(product => product)
    .sort(SortUtil.sortByOrder)
}

function getOptionTitles(productId, groupId, categoryId) {
  const { tm, te } = useI18n()
  const data = { franchise: {}, accident: {} }

  if (te(`content.categories.${categoryId}.accident`)) {
    data.accident = tm(`content.categories.${categoryId}.accident`)
  } else if (te(`content.groups.${groupId}.accident`)) {
    data.accident = tm(`content.groups.${groupId}.accident`)
  } else if (te(`content.products.${productId}.accident`)) {
    data.accident = tm(`content.products.${productId}.accident`)
  }

  if (te(`content.categories.${categoryId}.duration`)) {
    data.duration = tm(`content.categories.${categoryId}.duration`)
  } else if (te(`content.groups.${groupId}.duration`)) {
    data.duration = tm(`content.groups.${groupId}.duration`)
  } else if (te(`content.products.${productId}.duration`)) {
    data.duration = tm(`content.products.${productId}.duration`)
  }

  if (te(`content.categories.${categoryId}.franchise`)) {
    data.franchise = tm(`content.categories.${categoryId}.franchise`)
  } else if (te(`content.groups.${groupId}.franchise`)) {
    data.franchise = tm(`content.groups.${groupId}.franchise`)
  } else if (te(`content.products.${productId}.franchise`)) {
    data.franchise = tm(`content.products.${productId}.franchise`)
  }

  if (te(`content.categories.${categoryId}.amValue`)) {
    data.amValue = tm(`content.categories.${categoryId}.amValue`)
  } else if (te(`content.groups.${groupId}.amValue`)) {
    data.amValue = tm(`content.groups.${groupId}.amValue`)
  } else if (te(`content.products.${productId}.amValue`)) {
    data.amValue = tm(`content.products.${productId}.amValue`)
  }

  if (te(`content.categories.${categoryId}.gfValue`)) {
    data.gfValue = tm(`content.categories.${categoryId}.gfValue`)
  } else if (te(`content.groups.${groupId}.gfValue`)) {
    data.gfValue = tm(`content.groups.${groupId}.gfValue`)
  } else if (te(`content.products.${productId}.gfValue`)) {
    data.gfValue = tm(`content.products.${productId}.gfValue`)
  }

  return data
}

function getReplaceableProducts(productId) {
  if (isUpgrade(productId)) return []

  let replaceableProducts = []
  const categoryId = getCategoryIdFromProduct(productId)
  // products could not have been loaded yet, so we need to check for it
  if (!categoryId) return []
  const category = getCategory(categoryId)
  const groupId = getGroupIdFromProduct(productId)
  const group = getGroup(groupId)

  const reducer = (acc, value) => {
    acc.push(value.productId)
    return acc
  }

  if (category?.exclusive) {
    replaceableProducts = getProductsFromCategory(categoryId).reduce(reducer, [])
  } else if (group?.exclusive) {
    replaceableProducts = getProductsFromGroup(groupId).reduce(reducer, [])
  }

  return replaceableProducts
}

function willReplace(personId, productId) {
  const replaceableProducts = getReplaceableProducts(productId)
  return replaceableProducts.find(__productId => getProduct(personId, __productId)?.selected)
}

function getAVMId(productId) {
  return get(productAVMtypes, productId, '')
}

// Expose
export default function useProduct() {
  return {
    // cd
    categories,
    groups,
    products,
    selectedCategories,
    selectedProducts,

    // fn
    setProducts,
    fetchProducts,
    getAVMId,
    getCategoryIdFromProduct,
    getGroupFromProduct,
    getGroupsFromCategory,
    getGroupIdFromProduct,
    getOptionTitles,
    getProduct,
    getProductPrice,
    getProductsFromCategory,
    getProductsFromGroup,
    getReplaceableProducts,
    getSelectedProductsOfPerson,
    getSortedGroupsFromCategory,
    getSortedProductsFromCategory,
    getSortedProductsFromGroup,
    hasSelectedProducts,
    isUpgrade,
    composeOptions,
    createDefaultOffer,
    hasPersonKVG,
    hasPersonVVG,
    hasPersonOnlyKVG,
    hasPersonOnlyVVG,
    hasAccident,
    hasAccidentToggle,
    hasDuration,
    hasFranchise,
    hasVitalFranchise,
    hasOptions,
    isProductInBasket,
    isProductReloadNecessary,
    selectProductOption,
    selectProductOptionForCategory,
    setPriceCalculationsForProduct,
    transferProductToPerson,
    willReplace,
  }
}
